import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import type { To } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconAngleDown } from '@sorare/core/src/atoms/icons/system/iconAngleDown.ts';
import { iconAngleUp } from '@sorare/core/src/atoms/icons/system/iconAngleUp.ts';
import { Horizontal, SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';

import type { ChampionshipRaritySelect_so5UserGroup } from './__generated__/index.graphql.ts';

const Floating = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: calc(100% + var(--unit));
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  padding: var(--intermediate-unit) var(--double-unit);
  background: var(--c-black);
`;
const Option = styled(SBHorizontal)`
  cursor: pointer;
  gap: var(--unit);
  min-width: 320px;
  padding: var(--intermediate-unit) var(--double-unit);
  transition: 0.2s ease-in-out;
  .composable &:hover {
    background-color: var(--c-nd-50);
    + ${Floating} {
      display: initial;
    }
  }
`;
const OptionContainer = styled.div`
  position: relative;
  &:not(.composable) {
    opacity: 0.5;
    &:hover {
      opacity: 0.65;
    }
  }
`;

type Props = {
  selectUserGroupSlug: string;
  userGroups: ChampionshipRaritySelect_so5UserGroup[];
  to: (args: { competition: string; userGroup: string; user: string }) => To;
  allowNavigatingWhenLocked?: boolean;
  onClick?: (rarity: Rarity) => void;
};
export const ChampionshipRaritySelect = ({
  selectUserGroupSlug,
  userGroups,
  to: toProp,
  allowNavigatingWhenLocked = true,
  onClick,
}: Props) => {
  const selectSo5UserGroup =
    userGroups.find(({ slug }) => selectUserGroupSlug === slug) ||
    userGroups[0];
  const rarity = selectSo5UserGroup?.rarityType as Rarity;

  const so5UserGroupsPerRarity = userGroups.reduce(
    (acc, userGroup) => {
      const rarityType = (userGroup.mainRarityType ||
        userGroup.rarityType) as Rarity;
      acc[rarityType] = [...(acc[rarityType] || []), userGroup];
      return acc;
    },
    {} as Record<Rarity, ChampionshipRaritySelect_so5UserGroup[]>
  );

  const so5UserGroupsMatchingRarity = rarity
    ? so5UserGroupsPerRarity[rarity]
    : userGroups;

  if (!selectSo5UserGroup || (so5UserGroupsMatchingRarity?.length || 0) === 0) {
    return null;
  }

  const rarities = sortByRarity(
    Object.keys(so5UserGroupsPerRarity) as Rarity[]
  ).reverse();

  if (rarities.length === 1) {
    return null;
  }

  return (
    <Dropdown
      gap={4}
      label={({ open, ...btnProps }) => (
        <Button
          disableDebounce
          color="tertiary"
          size="small"
          endIcon={
            <FontAwesomeIcon icon={open ? iconAngleUp : iconAngleDown} />
          }
          className={classNames({ active: open })}
          {...btnProps}
        >
          <LabelL as={Horizontal}>
            <Horizontal as="span">
              <ScarcityIcon
                rarity={
                  (selectSo5UserGroup.mainRarityType ||
                    selectSo5UserGroup.rarityType) as Rarity
                }
              />
              {
                scarcityNames[
                  selectSo5UserGroup.mainRarityType ||
                    selectSo5UserGroup.rarityType
                ]
              }
            </Horizontal>
          </LabelL>
        </Button>
      )}
    >
      {({ closeDropdown }) => (
        <>
          {rarities.map(r => {
            const userGroup = so5UserGroupsPerRarity[r][0];
            const to = toProp({
              competition: userGroup.slug,
              userGroup: userGroup.slug,
              user: '',
            });

            return (
              <OptionContainer
                key={r}
                className={classNames({
                  composable: true,
                })}
              >
                <Option
                  {...(allowNavigatingWhenLocked
                    ? {
                        as: Link,
                        to,
                        replace: true,
                        onClick: () => {
                          onClick?.(r);
                          closeDropdown();
                        },
                      }
                    : {})}
                >
                  <Horizontal>
                    <ScarcityIcon rarity={r} />
                    <LabelL
                      color={
                        selectUserGroupSlug === userGroup.slug
                          ? 'var(--c-white)'
                          : 'var(--c-nd-600)'
                      }
                    >
                      {scarcityNames[r]}
                    </LabelL>
                  </Horizontal>
                </Option>
              </OptionContainer>
            );
          })}
        </>
      )}
    </Dropdown>
  );
};

ChampionshipRaritySelect.fragments = {
  so5UserGroup: gql`
    fragment ChampionshipRaritySelect_so5UserGroup on So5UserGroup {
      slug
      rarityType
      mainRarityType
    }
  ` as TypedDocumentNode<ChampionshipRaritySelect_so5UserGroup>,
};
